/**
 * Baut den Rumpf von `PATCH /contracts/{id}` — **nur** die tatsächlich
 * geänderten Felder, nie den kompletten Formularzustand (Befund K3,
 * `docs/work/004a-sollstellung-cdc84c/findings.md`).
 *
 * Seit Spec 04a sperrt der Server `base_rent_net_cents` und
 * `operating_cost_advance_net_cents` (409 `terms_locked`) sowie `tenant_id`
 * (409 `tenant_locked`), sobald der Vertrag eine Sollstellung trägt — aber
 * nur, wenn das jeweilige Feld **im Rumpf steht**. Ein `PATCH`, der ein
 * unverändertes gesperrtes Feld unnötig mitschickt (z. B. weil der Nutzer nur
 * die Kaution nachträgt), scheitert an einer Regel, die ihn gar nicht
 * betrifft — ein echter Bedienfehler, kein Testproblem. Ein `PATCH` sendet,
 * was geändert werden soll, nicht den ganzen Zustand.
 *
 * Reine Funktion, eigenes Modul wie `integer.ts` — leichter zu testen als
 * eingebettet in `ContractMasterData.tsx`.
 */

import { parseCents } from '@/lib/money'
import type { ContractUpdate } from '@/features/contracts/api'
import { parseIntegerInput } from '@/features/contracts/integer'

export interface ContractFormValues {
  readonly tenantId: string
  readonly tenantLabel: string
  readonly startsOn: string
  readonly baseRent: string
  readonly operatingCostAdvance: string
  readonly paymentDueDay: string
  readonly deposit: string
  readonly noticePeriodMonths: string
  readonly endsOn: string
  readonly terminatedOn: string
  readonly contractRef: string
}

/**
 * Vergleicht `form` gegen `initial` (den zuletzt vom Server bestätigten
 * Stand) und trägt ein Feld nur dann in den Rumpf ein, wenn sich sein
 * **Eingabewert** geändert hat. `tenantLabel` ist reine Anzeige (Zweitzeile
 * des `TenantPicker`) und steht nie im Rumpf.
 */
export function buildContractPatchBody(
  form: ContractFormValues,
  initial: ContractFormValues,
): ContractUpdate {
  const body: ContractUpdate = {}

  if (form.tenantId !== initial.tenantId) body.tenant_id = form.tenantId
  if (form.startsOn !== initial.startsOn) body.starts_on = form.startsOn
  if (form.baseRent !== initial.baseRent) body.base_rent_net_cents = parseCents(form.baseRent)
  if (form.operatingCostAdvance !== initial.operatingCostAdvance) {
    body.operating_cost_advance_net_cents = parseCents(form.operatingCostAdvance)
  }
  if (form.paymentDueDay !== initial.paymentDueDay) {
    body.payment_due_day = parseIntegerInput(form.paymentDueDay)
  }
  // Direkt `null` statt `clear` — funktioniert im Web für jedes leerbare Feld
  // (Spec 02a, „Felder leeren": `clear` ist die iOS-Ergänzung).
  if (form.deposit !== initial.deposit) body.deposit_cents = parseCents(form.deposit)
  if (form.noticePeriodMonths !== initial.noticePeriodMonths) {
    body.notice_period_months = parseIntegerInput(form.noticePeriodMonths)
  }
  if (form.endsOn !== initial.endsOn) body.ends_on = form.endsOn === '' ? null : form.endsOn
  if (form.terminatedOn !== initial.terminatedOn) {
    body.terminated_on = form.terminatedOn === '' ? null : form.terminatedOn
  }

  const contractRef = form.contractRef.trim()
  if (contractRef !== initial.contractRef.trim()) {
    body.contract_ref = contractRef === '' ? null : contractRef
  }

  return body
}
